import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Copy,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Button, cn } from '@mlm/ui';
import type { AssistantMessageDto } from '../../lib/assistant-api';

/**
 * One chat turn. Assistant bodies render as Markdown (react-markdown WITHOUT
 * rehype-raw, so raw HTML in the model output is escaped — XSS-safe by
 * construction). Metadata renders as affordances: numbered web-search Sources,
 * navigate buttons, a truncation note, and a per-message TTS toggle.
 */
export function MessageBubble({
  message,
  speakingId,
  onSpeak,
  onStopSpeak,
  ttsSupported,
  streaming = false,
  isLast = false,
  onFeedback,
  onRegenerate,
  busy = false,
}: {
  message: AssistantMessageDto;
  speakingId: string | null;
  onSpeak: (id: string, text: string) => void;
  onStopSpeak: () => void;
  ttsSupported: boolean;
  /** Still being written — hide the per-message affordances until it settles. */
  streaming?: boolean;
  /** Only the newest answer can be regenerated. */
  isLast?: boolean;
  onFeedback?: (value: 'up' | 'down' | null) => void;
  onRegenerate?: () => void;
  /** A send/regenerate is in flight — don't start a second one. */
  busy?: boolean;
}) {
  const mine = message.role === 'user';
  const meta = message.metadata ?? {};
  const speaking = speakingId === message.id;
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(message.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (insecure origin / denied) — nothing to recover */
    }
  }

  if (mine) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-o px-3 py-2 text-sm text-white">
          {message.body}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] space-y-2 rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm text-foreground">
        <div
          className={cn(
            'break-words leading-relaxed',
            '[&_a]:text-o [&_a]:underline',
            '[&_code]:rounded [&_code]:bg-background/60 [&_code]:px-1 [&_code]:text-[13px]',
            '[&_li]:ml-4 [&_ol]:list-decimal [&_ul]:list-disc',
            '[&_p+p]:mt-2 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold',
          )}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.body}</ReactMarkdown>
          {streaming && (
            <span
              className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 animate-pulse bg-foreground/70"
              aria-hidden
            />
          )}
        </div>

        {meta.truncated && (
          <p className="text-[11px] italic text-muted-foreground">
            (Answer was cut short — ask me to continue.)
          </p>
        )}

        {meta.incomplete && (
          <p className="text-[11px] italic text-muted-foreground">
            (I ran out of steps on that one — try asking it in a simpler way.)
          </p>
        )}

        {meta.refusal && (
          <p className="text-[11px] italic text-muted-foreground">
            (I couldn&apos;t help with that request.)
          </p>
        )}

        {Boolean(meta.navigate?.length) && (
          <div className="flex flex-wrap gap-2">
            {meta.navigate!.map((n) => (
              <Button key={n.route} asChild size="sm" variant="outline" className="h-7">
                <Link to={n.route}>
                  {n.label}
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            ))}
          </div>
        )}

        {Boolean(meta.citations?.length) && (
          <ol className="space-y-0.5 border-t pt-1.5 text-[11px] text-muted-foreground">
            {meta.citations!.map((c, i) => (
              <li key={c.url} className="truncate">
                {i + 1}.{' '}
                <a href={c.url} target="_blank" rel="noreferrer" className="underline">
                  {c.title || c.url}
                </a>
              </li>
            ))}
          </ol>
        )}

        {!streaming && (
          <div className="flex items-center justify-end gap-1 pt-0.5">
            <IconAction label={copied ? 'Copied' : 'Copy answer'} onClick={copy}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </IconAction>

            {onFeedback && (
              <>
                <IconAction
                  label={message.feedback === 'up' ? 'Remove your rating' : 'Good answer'}
                  active={message.feedback === 'up'}
                  onClick={() => onFeedback(message.feedback === 'up' ? null : 'up')}
                >
                  <ThumbsUp className="h-3.5 w-3.5" />
                </IconAction>
                <IconAction
                  label={message.feedback === 'down' ? 'Remove your rating' : 'Bad answer'}
                  active={message.feedback === 'down'}
                  onClick={() => onFeedback(message.feedback === 'down' ? null : 'down')}
                >
                  <ThumbsDown className="h-3.5 w-3.5" />
                </IconAction>
              </>
            )}

            {isLast && onRegenerate && (
              <IconAction label="Try again" disabled={busy} onClick={onRegenerate}>
                <RefreshCw className="h-3.5 w-3.5" />
              </IconAction>
            )}

            {ttsSupported && (
              <IconAction
                label={speaking ? 'Stop reading aloud' : 'Read aloud'}
                active={speaking}
                onClick={() => (speaking ? onStopSpeak() : onSpeak(message.id, message.body))}
              >
                {speaking ? (
                  <VolumeX className="h-3.5 w-3.5" />
                ) : (
                  <Volume2 className="h-3.5 w-3.5" />
                )}
              </IconAction>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Message-footer action. Kept faintly visible rather than hover-revealed —
 * hover-only affordances are invisible on touch, which is most of this app's
 * traffic.
 */
function IconAction({
  label,
  onClick,
  children,
  active = false,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'text-o' : 'text-muted-foreground/70 hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-40 hover:text-muted-foreground/70',
      )}
    >
      {children}
    </button>
  );
}
